import { Controller } from "@hotwired/stimulus"

/*
 * Gates saving an access point's Amazon Key details.
 *
 * Amazon needs driver notes before an access point is any use to it, so Save
 * waits for them — but only while the setting is actually switched on, since
 * this button saves the whole access point.
 *
 * A photo is what makes the access point usable by drivers, but it is never
 * demanded up front: a manager who does not have one yet still gets to save the
 * rest of the page. Switching the setting on without one is confirmed first, so
 * they know the photo is still outstanding. An access point Amazon already
 * unlocks is not asked again — that save is usually about something else
 * entirely, and the reminder would be the friction this removed.
 */
export default class extends Controller {
  static targets = ["enabled", "notes"]
  static values = {
    submitSelector: String,
    // Whether Amazon Key was already on for this access point when the page was
    // rendered, so the confirmation is only raised while it is being switched on.
    alreadyEnabled: Boolean,
    missingPhotoTitle: String,
    missingPhotoBody: String,
    missingPhotoAccept: String,
    missingPhotoCancel: String
  }

  connect() {
    this.refresh()
  }

  disconnect() {
    if (this.submitButton) this.submitButton.disabled = false
    this.clearMissingPhotoConfirmation()
  }

  refresh() {
    const submit = this.submitButton
    if (!submit) return

    // A locked checkbox is not something the manager can act on — the property
    // setting is off, or the door is offline — so an incomplete Amazon Key setup
    // behind it must not block them editing the rest of the access point.
    if (!this.sectionActive) {
      submit.disabled = false
      this.clearMissingPhotoConfirmation()
      return
    }

    submit.disabled = !this.notesTarget?.value.trim()
    this.updateMissingPhotoConfirmation()
  }

  // Turbo reads these off the submitter when the form is submitted.
  updateMissingPhotoConfirmation() {
    if (this.alreadyEnabledValue || this.hasPhoto) {
      this.clearMissingPhotoConfirmation()
      return
    }

    const submit = this.submitButton
    submit.dataset.turboConfirm = this.missingPhotoBodyValue
    submit.dataset.confirmTitle = this.missingPhotoTitleValue
    submit.dataset.confirmAccept = this.missingPhotoAcceptValue
    submit.dataset.confirmCancel = this.missingPhotoCancelValue
  }

  clearMissingPhotoConfirmation() {
    const submit = this.submitButton
    if (!submit) return

    delete submit.dataset.turboConfirm
    delete submit.dataset.confirmTitle
    delete submit.dataset.confirmAccept
    delete submit.dataset.confirmCancel
  }

  get sectionActive() {
    return this.hasEnabledTarget && this.enabledTarget.checked && !this.enabledTarget.disabled
  }

  get hasPhoto() {
    const preview = this.element.querySelector("[data-image-upload-target='imagePreview']")
    const fileInput = this.element.querySelector("[data-image-upload-target='fileInput']")
    const removeInput = this.element.querySelector("[data-image-upload-target='removeImageInput']")

    if (removeInput?.checked) return false
    if (fileInput?.files?.length > 0) return true

    return Boolean(preview?.getAttribute("src"))
  }

  get submitButton() {
    if (!this.hasSubmitSelectorValue) return null

    return document.querySelector(this.submitSelectorValue)
  }
}
